// 수정된 authStore.jsx - 초기 로그인 시 불필요한 refresh 호출 제거

import { create } from "zustand";
import { authApi } from "../api/auth.jsx";
import { setCookie, deleteCookie } from "../utils/cookie.jsx";
import { encryptString } from "../utils/cryptoUtils.js";
import {
  parseJWT,
  getLoginInfoFromToken,
  setAccessToken,
  getAccessToken,
  removeAccessToken,
  isTokenExpired,
  refreshJWT,
  debugToken,
} from "../utils/jwtUtil.js";

const useAuthStore = create((set, get) => ({
  // 상태
  isAuthenticated: false,
  loading: false,
  error: null,
  user: null,
  accessToken: null,
  publicKey: null,
  initialized: false,

  // 앱 초기화 (새로고침 시 자동 로그인 복구)
  initialize: async () => {
    try {
      set({ loading: true });

      // 이미 초기화된 경우 스킵
      if (get().initialized) {
        set({ loading: false });
        return;
      }

      // 1. 메모리에서 토큰 확인
      const storedToken = getAccessToken();
      if (storedToken && !isTokenExpired(storedToken)) {
        const userInfo = getLoginInfoFromToken(storedToken);
        set({
          isAuthenticated: true,
          accessToken: storedToken,
          user: userInfo
            ? {
                userUid: userInfo.userUid,
                signInId: userInfo.signInId,
                name: userInfo.name,
                roles: userInfo.roles || [],
              }
            : null,
          initialized: true,
          loading: false,
        });
        return;
      }

      // 2. 현재 경로가 로그인 페이지인 경우 refresh token 갱신 스킵
      const currentPath = window.location.pathname;
      const isLoginPage = currentPath === "/login" || currentPath === "/";

      if (!isLoginPage) {
        // 로그인 페이지가 아닌 경우에만 refresh token으로 갱신 시도

        try {
          const newToken = await refreshJWT();
          if (newToken) {
            const userInfo = getLoginInfoFromToken(newToken);
            set({
              isAuthenticated: true,
              accessToken: newToken,
              user: userInfo
                ? {
                    userUid: userInfo.userUid,
                    signInId: userInfo.signInId,
                    name: userInfo.name,
                    roles: userInfo.roles || [],
                  }
                : null,
              initialized: true,
              loading: false,
            });
            return;
          }
        } catch (refreshError) {
          // refresh token도 만료되었거나 없는 경우
        }
      } else {
      }

      // 3. 모든 토큰이 없거나 만료된 경우, 또는 로그인 페이지인 경우

      set({
        isAuthenticated: false,
        accessToken: null,
        user: null,
        initialized: true,
        loading: false,
      });
    } catch (error) {
      set({
        isAuthenticated: false,
        accessToken: null,
        user: null,
        initialized: true,
        loading: false,
      });
    }
  },

  // 로그인 초기화 (공개키 받기)
  initLogin: async () => {
    try {
      set({ loading: true, error: null });

      const response = await authApi.initLogin();

      if (response && response.publicKey) {
        set({
          publicKey: response.publicKey,
          loading: false,
        });
        return response;
      } else {
        throw new Error("공개키를 받지 못했습니다.");
      }
    } catch (error) {
      set({
        error:
          error.response?.data?.message ||
          error.message ||
          "로그인 초기화 실패",
        loading: false,
      });
      throw error;
    }
  },

  // 로그인 (JWT에서 사용자 정보 추출)
  login: async (credentials) => {
    try {
      set({ loading: true, error: null });

      const { publicKey } = get();
      if (!publicKey) {
        throw new Error("공개키가 없습니다. 로그인을 다시 시도해주세요.");
      }

      // RSA 암호화
      const encryptedUsername = await encryptString(
        publicKey,
        credentials.username
      );
      const encryptedPassword = await encryptString(
        publicKey,
        credentials.password
      );

      const response = await authApi.login({
        username: encryptedUsername,
        password: encryptedPassword,
      });

      // AccessToken만 받음
      if (response && response.accessToken) {
        // JWT 유틸리티를 사용하여 토큰 설정 및 사용자 정보 추출
        setAccessToken(response.accessToken);
        const userInfo = getLoginInfoFromToken(response.accessToken);

        if (!userInfo) {
          throw new Error("토큰에서 사용자 정보를 추출할 수 없습니다.");
        }

        // 디버깅용 (개발 환경에서만)
        if (process.env.NODE_ENV === "development") {
          debugToken(response.accessToken);
        }

        // Remember Me 처리
        if (credentials.rememberMe) {
          setCookie("savedUsername", credentials.username, 30);
        } else {
          deleteCookie("savedUsername");
        }

        set({
          isAuthenticated: true,
          accessToken: response.accessToken,
          user: {
            userUid: userInfo.userUid,
            signInId: userInfo.signInId,
            name: userInfo.name,
            roles: userInfo.roles || [],
          },
          loading: false,
          error: null,
          initialized: true,
        });

        return { accessToken: response.accessToken };
      } else {
        throw new Error(response.message || "로그인 실패");
      }
    } catch (error) {
      // 에러 메시지 처리
      let errorMessage = "로그인에 실패했습니다.";

      if (error.response?.data) {
        const errorData = error.response.data;

        if (errorData.success === false) {
          switch (errorData.error) {
            case "INVALID_CREDENTIALS":
              errorMessage = "ID와 Password가 일치하지 않습니다.";
              break;
            case "USERNAME_REQUIRED":
              errorMessage = "사용자 아이디를 입력해주세요.";
              break;
            case "PASSWORD_REQUIRED":
              errorMessage = "비밀번호를 입력해주세요.";
              break;
            case "USER_NOT_FOUND":
              errorMessage = "존재하지 않는 사용자입니다.";
              break;
            case "INVALID_PASSWORD":
              errorMessage = "ID와 Password가 일치하지 않습니다.";
              break;
            case "ACCOUNT_DISABLED":
              errorMessage = "로그인할 수 없습니다. 관리자에게 문의해주세요.";
              break;
            case "ACCOUNT_LOCKED":
              errorMessage = "로그인할 수 없습니다. 관리자에게 문의해주세요.";
              break;
            case "ERROR_LOGIN_DECRYPT":
              errorMessage =
                "보안 처리 중 오류가 발생했습니다. 다시 시도해주세요.";
              break;
            default:
              errorMessage = errorData.message || "로그인에 실패했습니다.";
          }
        } else {
          errorMessage = errorData.message || "로그인에 실패했습니다.";
        }
      } else if (error.message.includes("공개키")) {
        errorMessage = error.message;
      } else if (error.message.includes("암호화")) {
        errorMessage = "보안 처리 중 오류가 발생했습니다. 다시 시도해주세요.";
      } else if (error.message.includes("Failed to fetch")) {
        errorMessage =
          "서버에 연결할 수 없습니다. 네트워크 연결을 확인해주세요.";
      } else if (error.message.includes("토큰")) {
        errorMessage = "인증 처리 중 오류가 발생했습니다. 다시 시도해주세요.";
      }

      set({
        isAuthenticated: false,
        accessToken: null,
        user: null,
        error: errorMessage,
        loading: false,
      });

      return { error: errorMessage };
    }
  },

  // 토큰 갱신 (JWT 유틸리티 사용)
  refreshToken: async () => {
    try {
      const newToken = await refreshJWT();

      if (newToken) {
        // 새 토큰에서 사용자 정보 파싱
        const userInfo = getLoginInfoFromToken(newToken);

        set({
          accessToken: newToken,
          user: userInfo
            ? {
                userUid: userInfo.userUid,
                signInId: userInfo.signInId,
                name: userInfo.name,
                roles: userInfo.roles || [],
              }
            : null,
          error: null,
        });

        return newToken;
      } else {
        // throw new Error("토큰 갱신 실패");
      }
    } catch (error) {
      // 토큰 갱신 실패 시 로그아웃 처리
      removeAccessToken();
      set({
        isAuthenticated: false,
        accessToken: null,
        user: null,
        error: null,
        loading: false,
      });

      throw error;
    }
  },

  // 로그아웃
  logout: async () => {
    try {
      set({ loading: true });

      await authApi.logout();

      // JWT 유틸리티로 토큰 제거
      removeAccessToken();

      set({
        isAuthenticated: false,
        accessToken: null,
        user: null,
        error: null,
        loading: false,
        publicKey: null,
        initialized: true,
      });
    } catch (error) {
      // 로그아웃은 실패해도 로컬 상태는 초기화
      removeAccessToken();
      set({
        isAuthenticated: false,
        accessToken: null,
        user: null,
        error: null,
        loading: false,
        publicKey: null,
        initialized: true,
      });
    }
  },

  // 에러 초기화
  clearError: () => {
    set({ error: null });
  },

  // 인증 상태 확인
  checkAuth: () => {
    const token = getAccessToken();
    if (token && !isTokenExpired(token)) {
      set({ isAuthenticated: true });
      return true;
    } else {
      set({ isAuthenticated: false });
      return false;
    }
  },

  // 상태 초기화
  reset: () => {
    removeAccessToken();
    set({
      isAuthenticated: false,
      loading: false,
      error: null,
      user: null,
      accessToken: null,
      publicKey: null,
      initialized: false,
    });
  },
}));

export default useAuthStore;
